import {
	Card,
	CardContent,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	Typography,
} from '@mui/material';
import { FC } from 'react';
import { useSelector } from 'react-redux';
import {
	getConfirmedOrders,
	getConfirmedOrdersLoader,
	getOrderDetailsCombined,
	getOrderDetailsCombinedLoader,
} from 'redux/reducers/orders/selectors';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';
import { toNumber } from 'utils/toNumber';
import { TOrderDetailsCombinedReturn, TConfirmedOrdersRow } from 'redux/reducers/orders/types';

export const RefundAmountBreakdown: FC<{
	refundRow: TOrderDetailsCombinedReturn;
	orderRow: TConfirmedOrdersRow;
}> = ({ refundRow, orderRow }) => {
	const orderDetailsCombinedIsLoading = useSelector(getOrderDetailsCombinedLoader);
	const confirmedOrdersIsLoading = useSelector(getConfirmedOrdersLoader);

	const paymentCurrency = refundRow?.paymentCurrency ?? 'EUR';

	return (
		<div>
			{!!refundRow && !!orderRow && !orderDetailsCombinedIsLoading && !confirmedOrdersIsLoading && (
				<Card
					sx={{
						mb: 2,
						'&': {
							borderRadius: '12px',
						},
					}}
				>
					<CardContent>
						<Typography sx={{ fontSize: 14 }} color="primary" gutterBottom>
							Refund Amount Breakdown
						</Typography>
						<TableContainer>
							<Table>
								<TableBody>
									<TableRow>
										<TableCell sx={{ padding: '5px', borderBottom: 0 }}>
											Merchandise cost:
										</TableCell>
										<TableCell align="right" sx={{ padding: '5px', borderBottom: 0 }}>
											<AmountAndCurrency
												amount={toNumber(refundRow?.totalMerchReturned)}
												currency={paymentCurrency}
												size={0.9}
											/>
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell sx={{ padding: '5px', borderBottom: 0 }}>Delivery Cost:</TableCell>
										<TableCell align="right" sx={{ padding: '5px', borderBottom: 0 }}>
											<AmountAndCurrency
												amount={toNumber(refundRow?.totalDeliveryReturned)}
												currency={paymentCurrency}
												size={0.9}
											/>
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell sx={{ padding: '5px', borderBottom: 0 }}>Taxes:</TableCell>
										<TableCell align="right" sx={{ padding: '5px', borderBottom: 0 }}>
											<AmountAndCurrency
												amount={toNumber(refundRow?.totalVatReturned)}
												currency={paymentCurrency}
												size={0.9}
											/>
										</TableCell>
									</TableRow>

									<TableRow>
										<TableCell sx={{ padding: '5px', borderBottom: 0 }} />
										<TableCell sx={{ padding: '5px', borderBottom: 0 }} />
									</TableRow>
									<TableRow>
										<TableCell
											sx={{
												padding: '5px',
												fontWeight: 'bold',
												borderBottom: 0,
												fontSize: '1rem',
												color: 'primary.main',
											}}
										>
											Total Amount Refunded:
										</TableCell>
										<TableCell
											align="right"
											sx={{
												padding: '5px',
												fontWeight: 'bold',
												borderBottom: 0,
												fontSize: '1rem',
												color: 'primary.main',
											}}
										>
											<AmountAndCurrency
												amount={toNumber(refundRow?.totalReturnAmount)}
												currency={paymentCurrency}
												size={1}
												bold
											/>
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell sx={{ padding: '5px' }} />
										<TableCell sx={{ padding: '5px' }} />
									</TableRow>
									<TableRow>
										<TableCell sx={{ padding: '5px', borderBottom: 0 }} />
										<TableCell sx={{ padding: '5px', borderBottom: 0 }} />
									</TableRow>

									<TableRow>
										<TableCell
											sx={{
												padding: '5px',
												fontWeight: 'bold',
												borderBottom: 0,
												fontSize: '1rem',
												color: 'primary.main',
											}}
										>
											Total Order Amount:
										</TableCell>
										<TableCell
											align="right"
											sx={{
												padding: '5px',
												fontWeight: 'bold',
												borderBottom: 0,
												fontSize: '1rem',
												color: 'primary.main',
											}}
										>
											<AmountAndCurrency
												amount={
													toNumber(orderRow?.totalMerchCost) +
													toNumber(orderRow?.deliveryCost) +
													toNumber(orderRow?.totalVat) +
													toNumber(orderRow?.totalCustomDuties) +
													toNumber(orderRow?.easFeesDue) +
													toNumber(orderRow?.totalDpoFee)
												}
												currency={paymentCurrency}
												size={1}
												bold
											/>
										</TableCell>
									</TableRow>
								</TableBody>
							</Table>
						</TableContainer>
					</CardContent>
				</Card>
			)}
			{(orderDetailsCombinedIsLoading || confirmedOrdersIsLoading || !refundRow || !orderRow) && (
				<Skeleton variant="rectangular" height="300px" sx={{ borderRadius: '12px' }} />
			)}
		</div>
	);
};
